import React from "react";
import clsx from "clsx";
import {
  Calendar,
  Clock,
  Coffee,
  GraduationCap,
  Megaphone,
  Users,
} from "lucide-react";
import { formatToMonthDay } from "../../../../../utils/helpers.ts";
import { Link } from "react-router-dom";
import { DASHBOARD_CARD_MAX_ITEMS } from "../../../../../constants/dashboard-limits.ts";
import { Card } from "../../../../shared/card/card.tsx";
import {
  TrainingCategoryLabel,
  type TrainingCategory,
} from "../../../../shared/training-category-label/training-category-label.tsx";
import { Button } from "../../../../ui/index.ts";
import {
  useTrainingSessions,
  type DashboardTrainingSession,
} from "../../../../../hooks/use-training-sessions.ts";
import styles from "./training-development-card.module.css";

type SessionIcon = "graduation" | "coffee" | "megaphone" | "users";

const ICON_MAP = {
  graduation: GraduationCap,
  coffee: Coffee,
  megaphone: Megaphone,
  users: Users,
} as const;

function iconTileClass(icon: SessionIcon): string {
  switch (icon) {
    case "graduation":
      return styles.iconTileSky;
    case "coffee":
      return styles.iconTileAmber;
    case "megaphone":
      return styles.iconTileIndigo;
    case "users":
      return styles.iconTileGreen;
  }
} 

type TrainingDevelopmentCardProps = {
  /** When set, skips an internal fetch (parent should call `useTrainingSessions`). */
  sessions?: DashboardTrainingSession[];
};

export function TrainingDevelopmentCard({
  sessions: sessionsProp,
}: TrainingDevelopmentCardProps) {
  const { data } = useTrainingSessions({
    enabled: sessionsProp === undefined,
  });
  const sessions = sessionsProp ?? data;
  const total = sessions?.length || 0;
  const visible = (sessions || []).slice(0, DASHBOARD_CARD_MAX_ITEMS);
  const sessionLabel = total === 1 ? "Session" : "Sessions";

  return (
    <Card variant="default" padding="5xl"       className={styles.trainingDevelopmentCard}>
      <header className={styles.header}>
        <div className={styles.titleBlock}>
          <h2 className={clsx("heading-sm", styles.title)}>
            Training & Development
          </h2>
          <p className={clsx("sub-heading-sm", styles.subtitle)}>
            Upcoming learning sessions
          </p>
        </div>
        <p className={clsx("sub-heading-sm", styles.sessionCount)}>
          {total} {sessionLabel}
        </p>
      </header>

      <ul className={styles.list}>
        {visible.map((item) => {
          const iconKey: SessionIcon =
            item.eventType === "Classroom Session"
              ? "graduation"
              : item.eventType === "Coffee and Learn Sessions"
                ? "coffee"
                : item.eventType === "Salesforce Sessions"
                  ? "megaphone"
                  : item.eventType === "Mulesoft Meetups"
                    ? "users"
                    : "graduation";

          const Icon = ICON_MAP[iconKey];
          const dateObj = item.startDateTime
            ? new Date(item.startDateTime)
            : null;

          const isValidDate =
            dateObj instanceof Date && !isNaN(dateObj.getTime());
          return (
            <li key={item.id} className={styles.row}>
              <div
                className={clsx(styles.iconTile, iconTileClass(iconKey))}
                aria-hidden
              >
                <Icon
                  className={styles.iconTileGlyph}
                  size={20}
                  strokeWidth={2}
                />
              </div>
              <div className={styles.body}>
                <p className={clsx("heading-xs", styles.sessionTitle)}>
                  {item.title}
                </p>
                <div className={styles.meta}>
                  <span className={styles.metaItem}>
                    <Calendar
                      className={styles.metaIcon}
                      size={15}
                      strokeWidth={2}
                      aria-hidden
                    />
                    <span className="sub-heading-sm">
                      {formatToMonthDay(item.startDateTime) || "--"}
                    </span>
                  </span>
                  <span className={styles.metaItem}>
                    <Clock
                      className={styles.metaIcon}
                      size={15}
                      strokeWidth={2}
                      aria-hidden
                    />
                    <span className="sub-heading-sm">
                      {isValidDate
                        ? dateObj.toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "--"}
                    </span>
                  </span>
                  {/* <span className={styles.metaItem}>
                    <Users
                      className={styles.metaIcon}
                      size={15}
                      strokeWidth={2}
                      aria-hidden
                    />
                    <span className="sub-heading-sm">
                      {item.attendingCount} attending
                    </span>
                  </span> */}
                </div>
              </div>
              <div className={styles.labelCol}>
                <TrainingCategoryLabel
                  variant={item.category}
                  className={styles.categoryLabel}
                />
              </div>
            </li>
          );
        })}
      </ul>

   <Link to="/dashboard/view-announcement" style={{ textDecoration: "none" }}>
  <Button
    type="button"
    variant="muted-outline"
    fullWidth
    startIcon={<GraduationCap size={18} aria-hidden />}
    className={styles.footerButton}
  >
    View All Sessions
  </Button>
</Link>
    </Card>
  );
} 
   